import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Dialog, Stack, Box, Typography, IconButton, Button } from '@mui/material'
import { QRCodeSVG } from 'qrcode.react'
import CardActionButton from 'components/global/CardActions/CardActionButton'
import { trackEvent } from 'analytics'

interface IActionExportProps {
  ssid: {
    name: string
    passphrase?: string | null
    label: string
    authMethod?: 'open' | 'psk' | '802.1x'
  }
  analyticsSource?: 'wifi_management'
}

const ActionExport: React.FC<IActionExportProps> = ({ ssid, analyticsSource }) => {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)

  const escapeWifiField = (value: string) => value.replace(/([\\;,":])/g, '\\$1')
  const isWithoutPassphrase = ssid.authMethod === 'open' || ssid.authMethod === '802.1x'
  const wifiString = isWithoutPassphrase
    ? `WIFI:S:${escapeWifiField(ssid.name)};T:nopass;;`
    : `WIFI:S:${escapeWifiField(ssid.name)};T:WPA;P:${escapeWifiField(ssid.passphrase || '')};;`

  const handlePrint = () => {
    if (analyticsSource) {
      trackEvent(`${analyticsSource}_qrcode_printed`)
    }
    window.print()
  }

  return (
    <>
      <CardActionButton
        iconlabel="qrcode"
        iconweight="solid"
        iconsize="xs"
        tooltiptitle={t('event_ssids.actions.qrcode')}
        onClick={e => {
          e?.stopPropagation?.()
          if (analyticsSource) {
            trackEvent(`${analyticsSource}_qrcode_opened`)
          }
          setOpen(true)
        }}
        style={{
          border: '1px solid #EFEFEF',
          borderRadius: '8px',
          width: '24px',
          height: '24px',
          minWidth: '24px',
          minHeight: '24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#656565'
        }}
      />

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: '16px',
            p: 4,
            minWidth: '500px',
            maxWidth: '500px',
            '@media print': {
              minWidth: '100vw',
              maxWidth: '100vw',
              minHeight: '100vh',
              margin: 0,
              padding: '40px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }
          }
        }}>
        <Stack gap={3}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" className="no-print">
            <Typography sx={{ fontSize: 18, fontWeight: 600 }}>{t('event_ssids.qrcode.title')}</Typography>

            <IconButton
              size="small"
              onClick={() => setOpen(false)}
              sx={{
                p: 0,
                width: '24px',
                height: '24px',
                minWidth: '24px',
                minHeight: '24px',
                borderRadius: '50%',
                '&:hover .close-icon': {
                  backgroundColor: '#F7F7F7',
                  padding: '6px',
                  borderRadius: '50%'
                }
              }}>
              <Box
                className="close-icon"
                sx={{
                  width: '24px',
                  height: '24px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'background-color 0.15s, padding 0.15s'
                }}>
                <i className="fa-light fa-xmark" style={{ fontSize: 12 }} />
              </Box>
            </IconButton>
          </Stack>

          <Box
            sx={{
              border: '1px solid #E5E7EB',
              borderRadius: '16px',
              p: 4,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 3,
              '@media print': {
                transform: 'scale(1.5)',
                border: 'none'
              }
            }}>
            <Typography
              sx={{
                fontSize: 16,
                fontWeight: 600,
                textAlign: 'center',
                '@media print': {
                  fontSize: 32
                }
              }}>
              {ssid.label}
            </Typography>

            <QRCodeSVG value={wifiString} size={200} level="H" />

            <Stack gap={1.5} sx={{ width: '100%' }}>
              <Stack direction="row" justifyContent="space-between">
                <Typography
                  sx={{
                    fontSize: 14,
                    color: '#656565',
                    '@media print': {
                      fontSize: 22,
                      color: '#464646'
                    }
                  }}>
                  {t('event_ssids.qrcode.network')} :
                </Typography>
                <Typography
                  sx={{
                    fontSize: 14,
                    fontWeight: 600,
                    '@media print': {
                      fontSize: 22
                    }
                  }}>
                  {ssid.name}
                </Typography>
              </Stack>
              {!isWithoutPassphrase && (
                <Stack direction="row" justifyContent="space-between">
                  <Typography
                    sx={{
                      fontSize: 14,
                      color: '#656565',
                      '@media print': {
                        fontSize: 22,
                        color: '#464646'
                      }
                    }}>
                    {t('event_ssids.qrcode.password')} :
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 14,
                      fontWeight: 600,
                      fontFamily: 'monospace',
                      '@media print': {
                        fontSize: 22
                      }
                    }}>
                    {ssid.passphrase}
                  </Typography>
                </Stack>
              )}
            </Stack>
          </Box>

          <Stack direction="row" gap={1} justifyContent="flex-end" className="no-print">
            <Button
              variant="outlined"
              onClick={handlePrint}
              startIcon={<i className="fa-regular fa-print" style={{ fontSize: 10 }} />}
              sx={{
                borderColor: '#EFEFEF',
                color: '#464646',
                fontSize: 11,
                textTransform: 'none',
                height: '32px',
                minHeight: '32px',
                px: 2,
                py: 0,
                '&:hover': {
                  borderColor: '#EFEFEF',
                  backgroundColor: '#F7F7F7'
                }
              }}>
              {t('event_ssids.actions.print')}
            </Button>
          </Stack>
        </Stack>
        <style>
          {`
            @media print {
              .no-print {
                display: none !important;
              }
            }
          `}
        </style>
      </Dialog>
    </>
  )
}

export default ActionExport
